(ns app.front.form.subs
  (:require [app.domain.form.i18n :as form-i18n]
            [app.domain.form.model :as form-model]
            [app.domain.licence.renewal-model :as renewal-model]
            [app.domain.util :as du]
            [app.front.acceptance.subs :as acceptance-subs]
            [app.front.entities :as entities]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.feature-flags :as ff]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(defn- data-forms
  "The wizard 'data' forms of a phase: excludes sign-off forms and forms with a nil data-schema
   (can happen when exporting field-config-filtered schemas), sorted by schema name."
  [forms]
  (->> forms
       (remove #(-> % :form/schema :form.schema/type (= :form.schema.type/sign-off)))
       (filter (comp :form.schema/data-schema :form/schema))
       (sort-by (comp :form.schema/name :form/schema))))

(rf/reg-sub ::application-forms
  (fn [[_ application]]
    (rf/subscribe [::entities/entities {:entity/type   :entity.type/form
                                        :entity/parent (-> application :db/id)}]))
  :-> data-forms)


(defn- on-forms-step? [route-match]
  (and (= (get-in route-match [:data :name]) :view/application-wizard-workspace)
       (= (get-in route-match [:path-params :step]) "forms")))

(rf/reg-sub ::expanded-form-id :-> ::expanded-form-id)

;; Form context for NoVa's FillFormFields/GetFormFields tools: the expanded form (if any) plus a
;; summary of every form on the application.
(rf/reg-sub ::forms-step-page-context
  (fn [_ _]
    [(rf/subscribe [:route/match])
     (rf/subscribe [::ws-subs/workspace-target])
     (rf/subscribe [::expanded-form-id])
     (rf/subscribe [:language])
     (rf/subscribe [::entities/context])
     (rf/subscribe [::ff/enabled? :feature-flag.id/nova-form-filler])])
  (fn [[route-match application expanded-form-id language ctx form-filler?] _]
    (when (and form-filler? application (on-forms-step? route-match))
      (p/assoc-if
        {:forms (->> (entities/find-entities ctx {:entity/type   :entity.type/form
                                                  :entity/parent (:db/id application)})
                     data-forms
                     (mapv (fn [form]
                             {:form-id  (:db/id form)
                              :name     (form-model/form-label form language)
                              :complete (boolean (form-model/form-required-fields-set? form))})))}
        :expanded-form-id expanded-form-id))))


(rf/reg-sub ::phase-forms-count
  (fn [[_ phase]]
    (rf/subscribe [::application-forms phase]))
  :-> count)


(rf/reg-sub ::has-forms?
  (fn [[_ application]]
    (rf/subscribe [::application-forms application]))
  (fn [forms _]
    (-> forms seq boolean)))


(rf/reg-sub ::forms-inline?
  (fn [[_ target] _]
    (rf/subscribe [::application-forms target]))
  :-> form-model/forms-inline?)

(defn find-latest [acceptances form]
  (->> acceptances
       (filter (p/where-fn {:entity/parent form}))
       (remove (du/in-state-fn :acceptance.state/draft))
       du/latest-entity))


(defn acceptance-waiting-customer? [acceptances form]
  (let [{:acceptance/keys [value
                           customer-value]} (find-latest acceptances form)]
    (and (= :acceptance.value/needs-changes value)
         (or (nil? customer-value)
             (= :acceptance.customer-value/waiting-for-customer customer-value)))))


(def waiting-for-customer-state?
  (du/in-state-fn :state.application/draft
                  :state.application/pre-submit-payment))

(def in-a-review-state?
  (du/in-state-fn :state.application/in-review
                  :state.application/changes-requested))


(def form-confirmation-state?
  (du/in-state-fn :state.application/draft
                  :state.application/submission-awaiting-sign-off
                  :pre-consultation.state/draft))


(defn form-confirmation-active? [phase form-confirmation?]
  (and form-confirmation?
       (form-confirmation-state? phase)))

(defn waiting-for-customer? [application acceptances form]
  (or (and (in-a-review-state? application)
           (acceptance-waiting-customer? acceptances form))
      (waiting-for-customer-state? application)))


(def missing-data?
  (complement form-model/form-required-fields-set?))


(defn form-handler-fn [pred]
  (fn [[forms all-acceptances] [_ application]]
    (->> forms
         (filter (partial pred application all-acceptances))
         (map (juxt identity missing-data?)))))


(defn forms-and-acceptances-signal [[_ application]]
  [(rf/subscribe [::application-forms application])
   (rf/subscribe [::entities/entities {:entity/type :entity.type/acceptance}])])


(rf/reg-sub ::application-data-forms
  forms-and-acceptances-signal
  (form-handler-fn (complement waiting-for-customer?)))


(rf/reg-sub ::waiting-for-customer-forms
  forms-and-acceptances-signal
  (form-handler-fn waiting-for-customer?))

(rf/reg-sub ::missing-any-data?
  (fn [[_ parent]]
    [(rf/subscribe [::application-data-forms parent])
     (rf/subscribe [::waiting-for-customer-forms parent])])
  (fn [[data-forms waiting-forms] _]
    (let [missing-any-data? (partial some second)]
      (or (missing-any-data? data-forms)
          (missing-any-data? waiting-forms)))))

(rf/reg-sub ::tr-schema
  :<- [:language]
  (fn [language [_ tr]]
    ;; The fallback tr function might be group/workspace specific, so let the caller decide
    (fn [schema-opts]
      (form-i18n/tr-schema language tr schema-opts))))

(defn- fetch-form-field-info [db form-id request-id]
  {:comm/command {:command         :application/form-field-updates
                  :data            (util/mmap form-id)
                  :on-success      [::navigate-form.success form-id request-id]
                  :on-bad-request  [::navigate-form.failure form-id request-id]
                  :on-error        [::navigate-form.failure form-id request-id]
                  :on-server-error [::navigate-form.failure form-id request-id]}
   :db           (-> db
                     (update ::form-fields-fetching (fnil conj #{}) form-id)
                     (assoc-in [::form-field-info-request-id form-id] request-id))})

(rf/reg-event-fx ::navigate-form
  (fn [{:keys [db]} [_ {:keys [form-id]}]]
    (fetch-form-field-info (-> db
                               (assoc ::current-form-id form-id)
                               (update ::form-fields-updated dissoc form-id)
                               (update ::form-opening-values dissoc form-id)
                               (update ::form-opening-changes dissoc form-id)
                               (update ::pending-field-values dissoc form-id))
                           form-id (random-uuid))))

(defn- forms-affected-by-edit [db form-id]
  (let [ctx      (entities/db->context db)
        phase-id (some-> (entities/find-entity ctx form-id) :entity/parent :db/id)]
    (if phase-id
      ;; Schema and condition dependencies can share phase fields without an explicit form link.
      (->> (concat (keys (::form-fields-updated db)) (keys (::form-field-info-request-id db)))
           (into #{form-id})
           (filter #(= phase-id (some-> (entities/find-entity ctx %) :entity/parent :db/id))))
      #{form-id})))

(rf/reg-event-fx ::refresh-form-field-info
  (fn [{:keys [db]} [_ form-id]]
    ;; Initial navigation still fetches legacy change-request highlighting regardless of this flag.
    (when (ff/enabled? :feature-flag.id/form-field-change-metadata)
      (reduce (fn [{:keys [db fx]} id]
                (let [request (fetch-form-field-info db id (random-uuid))]
                  {:db (:db request)
                   :fx (conj fx [:comm/command (:comm/command request)])}))
              {:db db :fx []}
              (forms-affected-by-edit db form-id)))))

(rf/reg-event-db ::navigate-form.success
  (fn [db [_ form-id request-id status content]]
    (if (and (= 200 status)
             (= request-id (get-in db [::form-field-info-request-id form-id])))
      (-> db
          (update ::form-fields-fetching disj form-id)
          (assoc-in [::form-fields-updated form-id] content)
          (update ::form-field-info-request-id dissoc form-id))
      db)))

(rf/reg-event-db ::navigate-form.failure
  (fn [db [_ form-id request-id]]
    (if (= request-id (get-in db [::form-field-info-request-id form-id]))
      (-> db
          (update ::form-fields-fetching disj form-id)
          (update ::form-field-info-request-id dissoc form-id))
      db)))

(rf/reg-sub ::form-fields-fetching
  (fn [db _]
    (::form-fields-fetching db #{})))

(rf/reg-sub ::field-changes-fetching-for-form?
  :<- [::form-fields-fetching]
  (fn [fields-fetching [_ form-id]]
    (contains? fields-fetching form-id)))

(rf/reg-sub ::form-fields-updated
  (fn [db _]
    (::form-fields-updated db {})))

(rf/reg-sub ::field-changes-initial-loading-for-form?
  (fn [[_ form-id]]
    [(rf/subscribe [::field-changes-fetching-for-form? form-id])
     (rf/subscribe [::form-fields-updated])])
  (fn [[fetching? form-id->field-info] [_ form-id]]
    (and fetching? (not (contains? form-id->field-info form-id)))))

(rf/reg-sub ::form-field-info
  :<- [::form-fields-updated]
  (fn [form-id->field-info [_ form-id form-field-entity-id]]
    (get-in form-id->field-info [form-id form-field-entity-id])))

(rf/reg-sub ::form-field-updated?
  (fn [[_ form-id form-field-entity-id]]
    (rf/subscribe [::form-field-info form-id form-field-entity-id]))
  :-> :updated?)

(defn- field-token [field]
  (let [parent (:entity/parent field)]
    ;; Follow the field path so table cells match by row position, even after rows are recreated.
    [(if (= :entity.type/form-field (:entity/type parent))
       (field-token parent)
       (:db/id parent))
     (form-model/field-comparison-key field)]))

(defn- comparison-context [form]
  {:visible-field-ids (du/entity-id-set (form-model/get-visible-form-fields form))
   :table-column-keys (form-model/visible-table-column-keys form)})

(defn- session-value [field {:keys [visible-field-ids table-column-keys] :as context}]
  (case (:form.field/kind field)
    :form.field.kind/val (str-util/blank->nil (:form.field/value field))
    ;; Masked and revealed representations are deliberately excluded from comparisons.
    :form.field.kind/encrypted-personal-id nil
    :form.field.kind/file-seq (let [file (form-model/file-input-latest-file field)]
                                (or (:file/md5 file) (:db/id file)))
    ;; Compare values, not entity IDs or change metadata, so structural changes and reversions
    ;; also update the containing table's pill.
    (form-model/collection-value
      (filter #(and (not= :form.field.kind/encrypted-personal-id (:form.field/kind %))
                    (or (nil? (:form.field/id %)) (contains? visible-field-ids (:db/id %))))
              (:form.field/_parent field))
      #(session-value % context)
      (get table-column-keys (-> field :entity/parent :db/id)))))

(defn capture-form-opening-values [db form-id]
  (if (contains? (::form-opening-values db) form-id)
    db
    (let [form    (entities/find-entity (entities/db->context db) form-id)
          context (comparison-context form)
          fields  (form-model/get-form-fields-raw form)]
      (-> db
          (assoc-in [::form-opening-values form-id]
                    (into {} (map (juxt field-token #(session-value % context))) fields))
          ;; Freeze attribution before the first edit, including missing metadata, so a late save response
          ;; cannot attribute an opening value to someone who changed it during this session.
          (assoc-in [::form-opening-changes form-id]
                    (into {} (map (fn [field]
                                    [(field-token field)
                                     (get-in db [::form-fields-updated form-id (:db/id field) :last-value-change])]))
                          fields))))))

(rf/reg-event-db ::capture-opening-values
  (fn [db [_ form-id]]
    (capture-form-opening-values db form-id)))

(defn update-field-token [db {:keys [form-id form-field-id field-key parent-field-id]}]
  (let [ctx   (entities/db->context db)
        field (when form-field-id (entities/find-entity ctx form-field-id))]
    (if field
      (field-token field)
      (field-token {:entity/parent (entities/find-entity ctx
                                                         (or parent-field-id
                                                             (-> (entities/find-entity ctx form-id) :entity/parent :db/id)))
                    :form.field/id field-key}))))

(defn track-field-edit [db {:keys [form-id new-value] :as params} operation-id]
  (let [db (capture-form-opening-values db form-id)]
    (if (contains? params :new-value)
      (assoc-in db [::pending-field-values form-id (update-field-token db params)]
                {:operation-id operation-id :value (str-util/blank->nil new-value)})
      db)))

(rf/reg-event-db ::finish-field-edit
  (fn [db [_ form-id token operation-id]]
    (if (= operation-id (get-in db [::pending-field-values form-id token :operation-id]))
      (update-in db [::pending-field-values form-id] dissoc token)
      db)))

(rf/reg-sub ::form-opening-values :-> ::form-opening-values)
(rf/reg-sub ::form-opening-changes :-> ::form-opening-changes)
(rf/reg-sub ::pending-field-values :-> ::pending-field-values)

(rf/reg-sub ::renewal-form?
  (fn [[_ form-id]]
    (rf/subscribe [::entities/entity form-id]))
  (fn [form]
    (-> form :entity/parent :entity/parent renewal-model/renewal-workspace?)))

(rf/reg-sub ::form-comparison-context
  (fn [[_ form-id]]
    (rf/subscribe [::entities/entity form-id]))
  :-> comparison-context)

(rf/reg-sub ::form-field-session-changed?
  (fn [[_ form-id field-id]]
    [(rf/subscribe [::entities/entity field-id])
     (rf/subscribe [::form-comparison-context form-id])
     (rf/subscribe [::form-opening-values])
     (rf/subscribe [::pending-field-values])])
  (fn [[field context opening-by-form pending-by-form] [_ form-id]]
    (when (and field (contains? opening-by-form form-id))
      (let [token   (field-token field)
            pending (get-in pending-by-form [form-id token])
            value   (if pending
                      (:value pending)
                      (session-value field context))]
        (form-model/field-values-differ? (get-in opening-by-form [form-id token]) value)))))

(rf/reg-sub ::form-field-pill-attention?
  (fn [[_ form-id field-id]]
    [(rf/subscribe [::entities/entity field-id])
     (rf/subscribe [::form-field-info form-id field-id])
     (rf/subscribe [::renewal-form? form-id])
     (rf/subscribe [::form-field-session-changed? form-id field-id])])
  (fn [[field info renewal? session-changed?]]
    ;; Revealing an ID changes its frontend representation, not the value users should compare.
    (when-not (= :form.field.kind/encrypted-personal-id (:form.field/kind field))
      (or (:different-from-previous? info)
          (and (not renewal?) session-changed?)))))

(rf/reg-sub ::form-field-baseline-change
  (fn [[_ form-id field-id]]
    [(rf/subscribe [::entities/entity field-id])
     (rf/subscribe [::form-field-info form-id field-id])
     (rf/subscribe [::renewal-form? form-id])
     (rf/subscribe [::form-opening-changes])])
  (fn [[field info renewal? opening-by-form] [_ form-id]]
    (if renewal?
      (:baseline-last-value-change info)
      (get-in opening-by-form [form-id (field-token field)]))))

(rf/reg-sub ::some-form-field-updated?
  :<- [::form-fields-updated]
  (fn [form-id->field-info [_ form-id]]
    (->> (get form-id->field-info form-id)
         vals
         (some :updated?))))

;; Bumped after NoVa fills form fields while open. Form inputs only seed their local state from
;; the entity on mount, so this generation is used in the form's React key to force a remount.
(rf/reg-event-db ::bump-ai-fill-generation
  (fn [db _]
    (update db ::ai-fill-generation (fnil inc 0))))

(rf/reg-sub ::ai-fill-generation
  (fn [db _]
    (::ai-fill-generation db 0)))

(rf/reg-sub ::current-form-id :-> ::current-form-id)

(rf/reg-sub ::current-form
  :<- [::entities/context]
  :<- [::current-form-id]
  (fn [[ctx current-form-id]]
    (entities/find-entity ctx current-form-id)))

(rf/reg-sub ::previous-next-forms
  :<- [::current-form]
  (fn [form _]
    (let [parent (:entity/parent form)
          forms  @(rf/subscribe [::application-forms parent])]
      (util/find-previous-and-next (map :db/id forms) (:db/id form)))))

(rf/reg-sub ::form-status-color-code
  (fn [[_ form-id]]
    [(rf/subscribe [::entities/entity form-id])
     (rf/subscribe [::acceptance-subs/entity-latest-acceptance form-id])
     (rf/subscribe [::ff/enabled? :feature-flag.id/form-confirmation])])
  (fn [[form acceptance form-confirmation?] _]
    (let [active? (and (form-confirmation-active? (:entity/parent form) form-confirmation?)
                       (form-model/form-ai-updated-at form))]
      (cond
        (= :acceptance.customer-value/waiting-for-customer (-> acceptance :acceptance/customer-value))
        :warning

        (not (form-model/form-required-fields-set? form))
        :warning

        (and active?
             (not (form-model/form-confirmation-satisfied? form nil)))
        :attention

        (and (not active?)
             (-> form :latest-change nil?))
        :default

        (or (not active?)
            (form-model/form-confirmation-satisfied? form nil))
        :success

        :else
        :default))))


(rf/reg-sub ::form-confirmation-active?
  (fn [[_ form-id]]
    [(rf/subscribe [::entities/entity form-id])
     (rf/subscribe [::ff/enabled? :feature-flag.id/form-confirmation])])
  (fn [[form form-confirmation?] _]
    (and (form-confirmation-active? (:entity/parent form) form-confirmation?)
         (form-model/form-ai-updated-at form))))

(rf/reg-sub ::form-missing-mandatory-fields?
  (fn [[_ form-id]]
    (rf/subscribe [::entities/entity form-id]))
  (fn [form]
    (-> form form-model/form-required-fields-set? not)))

(rf/reg-sub ::form-confirmed?
  (fn [[_ form-id]]
    (rf/subscribe [::entities/entity form-id]))
  :-> form-model/form-confirmed?)


(rf/reg-sub ::all-forms-confirmed?
  ;; True when form confirmation is disabled or every applicant-writable, AI-updated form has been confirmed.
  (fn [[_ application]]
    [(rf/subscribe [::application-forms application])
     (rf/subscribe [::ff/enabled? :feature-flag.id/form-confirmation])])
  (fn [[forms form-confirmation?] [_ application]]
    (or (not (form-confirmation-active? application form-confirmation?))
        (every? #(form-model/form-confirmation-satisfied? % nil) forms))))

(rf/reg-sub ::all-wizard-forms-confirmed?
  (fn [[_ application]]
    (rf/subscribe [::all-forms-confirmed? application]))
  :-> identity)

(rf/reg-sub ::form-wizard-status-color-code
  (fn [[_ form-id]]
    (rf/subscribe [::form-status-color-code form-id]))
  :-> identity)

(defn- compute-form-field-progress [form]
  (let [tuples (->> (form-model/get-field-attrs+field-tuples form)
                    (util/distinct-by (comp :db/id second))
                    (remove (comp :form.field/inactive second)))
        total  (count tuples)
        filled (->> tuples
                    (filter (fn [[field-attrs field]]
                              (form-model/value-set? field field-attrs)))
                    count)]
    {:filled filled :total total}))

(rf/reg-sub ::form-field-progress-snapshot
  (fn [db [_ form-id]]
    (get-in db [::form-field-progress-snapshot form-id])))

(rf/reg-event-db ::snapshot-form-field-progress
  (fn [db [_ form-id]]
    (let [ctx      (entities/db->context db)
          progress (some-> (entities/find-entity ctx form-id)
                           compute-form-field-progress)
          current  (get-in db [::form-field-progress-snapshot form-id])]
      (if (= current progress)
        db
        (assoc-in db [::form-field-progress-snapshot form-id] progress)))))

(defn- workspace-loaded? [db]
  (let [status (get-in db [:view/workspace :fetch-status])]
    (and status (not= :fetching status))))

(rf/reg-event-fx ::enter-forms-step
  (fn [{:keys [db]} _]
    (when (on-forms-step? (:route/match db))
      (if (workspace-loaded? db)
        (let [ctx      (entities/db->context db)
              forms    (data-forms (entities/find-entities ctx
                                                           {:entity/type   :entity.type/form
                                                            :entity/parent (get-in db [:view/workspace :application-id])}))
              expanded (::expanded-form-id db)]
          {:db (reduce (fn [db form]
                         (let [progress (compute-form-field-progress form)]
                           (assoc-in db [::form-field-progress-snapshot (:db/id form)] progress)))
                       db
                       forms)
           :fx (when (some #(= expanded (:db/id %)) forms)
                 [[:dispatch [::navigate-form {:form-id expanded}]]])})
        {:dispatch-later [{:ms 100 :dispatch [::enter-forms-step]}]}))))

(rf/reg-sub ::form-field-progress-live
  (fn [[_ form-id]]
    (rf/subscribe [::entities/entity form-id]))
  (fn [form _]
    (when form
      (compute-form-field-progress form))))

(rf/reg-sub ::form-field-progress
  (fn [[_ form-id expanded?]]
    (if expanded?
      (rf/subscribe [::form-field-progress-live form-id])
      (rf/subscribe [::form-field-progress-snapshot form-id])))
  (fn [result _]
    result))

(rf/reg-sub ::current-field-config-map
  :<- [::current-form]
  :-> form-model/form->field-config-map)

(rf/reg-sub ::field-visible?
  :<- [::current-form]
  :<- [::current-field-config-map]
  (fn [[form field-config-map] [_ field-attr-from-schema]]
    (form-model/visible? field-config-map form field-attr-from-schema)))
